import React from 'react';
import { formatDistanceToNow } from 'date-fns';

interface ContinueOrNewDialogProps {
  cartridgeName: string;
  progressInstances: Array<{
    instanceId: string;
    branchName: string;
    lastActivity: string;
    commitCount: number;
  }>;
  onContinue: (instanceId: string) => void;
  onNewGame: () => void;
  onCancel: () => void;
}

export const ContinueOrNewDialog: React.FC<ContinueOrNewDialogProps> = ({
  cartridgeName,
  progressInstances,
  onContinue,
  onNewGame,
  onCancel
}) => {
  // Sort instances by last activity (most recent first)
  const sortedInstances = [...progressInstances].sort((a, b) => 
    new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime()
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-slate-800 border border-cyan-500/20 rounded-lg p-6 max-w-2xl max-h-[80vh] overflow-y-auto shadow-xl">
        <h2 className="text-2xl font-bold text-cyan-300 mb-4">Continue Your Journey?</h2>
        
        <p className="text-slate-300 mb-6">
          You have existing progress in <span className="text-cyan-400 font-semibold">{cartridgeName}</span>. 
          Would you like to continue from where you left off or start a new adventure?
        </p>

        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold text-slate-200">Your Progress:</h3>
          
          {sortedInstances.map((instance, index) => (
            <div 
              key={instance.instanceId}
              className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 hover:border-cyan-500/50 transition-colors cursor-pointer"
              onClick={() => onContinue(instance.instanceId)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-cyan-300 font-medium mb-1">
                    Session {sortedInstances.length - index}
                    {index === 0 && <span className="ml-2 text-xs bg-cyan-600/20 text-cyan-400 px-2 py-1 rounded">Most Recent</span>}
                  </h4>
                  <p className="text-sm text-slate-400">
                    Last played: {formatDistanceToNow(new Date(instance.lastActivity), { addSuffix: true })}
                  </p>
                  <p className="text-sm text-slate-400">
                    Progress: {instance.commitCount} {instance.commitCount === 1 ? 'decision' : 'decisions'} made
                  </p>
                </div>
                <button
                  className="text-cyan-400 hover:text-cyan-300 transition-colors"
                  aria-label="Continue this session"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onNewGame}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg"
          >
            Start New Adventure
          </button>
          
          <button
            onClick={onCancel}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};